// src/components/dashboard/FinanceChartV10.jsx — vFinal
//
// ОБНОВЛЕНЫ статические даты кризисов/восстановлений:
// seed_demo_vFinal.sql: кризисы 5, 13, 22 апреля 2026
// Было (seed_demo_v8.8): кризисы 12, 21, 28 марта 2026
//
// Динамические зоны из riskHistory (buildDynamicZones) остаются
// и будут корректны с любым seed — статические используются только как fallback.

import {
  ComposedChart, Area, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, ReferenceLine, ReferenceArea,
} from "recharts";

// Обновлены на seed_demo_vFinal.sql (апрель 2026)
const STATIC_CRISIS_ZONES   = [
  { x1: "2026-04-05", x2: "2026-04-07" },
  { x1: "2026-04-13", x2: "2026-04-15" },
  { x1: "2026-04-22", x2: "2026-04-24" },
];
const STATIC_RECOVERY_ZONES = [
  { x1: "2026-04-06", x2: "2026-04-09" },
  { x1: "2026-04-14", x2: "2026-04-17" },
  { x1: "2026-04-23", x2: "2026-04-26" },
];
const STATIC_ACTION_DATES   = ["2026-04-05","2026-04-13","2026-04-22"];
const STATIC_CRISIS_DATES   = ["2026-04-05","2026-04-13","2026-04-22"];
const STATIC_RECOVERY_DATES = ["2026-04-06","2026-04-14","2026-04-23"];

function buildDynamicZones(riskHistory) {
  if (!Array.isArray(riskHistory) || !riskHistory.length) return null;
  const sorted = [...riskHistory].sort((a, b) => (a.date ?? "").localeCompare(b.date ?? ""));
  const isCrisis    = s => s === "critical" || s === "red"  || s === "high";
  const isRecovered = s => s === "low"      || s === "green";

  const crisisDates   = [], recoveryDates  = [];
  const crisisZones   = [], recoveryZones  = [];
  let inCrisis = false, crisisStart = null;
  let inRecovery = false, recStart = null;

  for (const row of sorted) {
    const d = row.date, s = row.risk_status;
    if (isCrisis(s)) {
      if (!inCrisis) { inCrisis = true; crisisStart = d; }
      crisisDates.push(d);
      if (inRecovery) { recoveryZones.push({ x1: recStart, x2: d }); inRecovery = false; recStart = null; }
    } else if (isRecovered(s)) {
      if (inCrisis) {
        crisisZones.push({ x1: crisisStart, x2: d });
        inCrisis = false; crisisStart = null;
        if (!inRecovery) { inRecovery = true; recStart = d; }
      }
      recoveryDates.push(d);
    } else {
      if (inCrisis)   { crisisZones.push({ x1: crisisStart, x2: d });   inCrisis = false; }
      if (inRecovery) { recoveryZones.push({ x1: recStart,  x2: d });   inRecovery = false; }
    }
  }
  const last = sorted[sorted.length - 1]?.date;
  if (inCrisis   && crisisStart) crisisZones.push({ x1: crisisStart, x2: last });
  if (inRecovery && recStart)    recoveryZones.push({ x1: recStart,  x2: last });

  return {
    crisisZones:   crisisZones.slice(0, 4),
    recoveryZones: recoveryZones.slice(0, 4),
    actionDates:   crisisDates.slice(0, 8),
    crisisDates:   crisisDates.slice(0, 5),
    recoveryDates,
  };
}

function fmtRoi(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  const pct = Math.abs(n) < 10 ? n * 100 : n;
  return (pct > 0 ? "+" : "") + pct.toFixed(1) + "%";
}

function ChartTooltip({ active, payload, crisisDates, actionDates, recoveryDates }) {
  if (!active || !payload?.length) return null;
  const date       = payload[0]?.payload?.date ?? "";
  const isCrisis   = crisisDates.includes(date);
  const isAction   = actionDates.includes(date) && !isCrisis;
  const isRecovery = recoveryDates.includes(date);

  return (
    <div style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "10px 14px", fontFamily: "var(--font-mono)", fontSize: 11, minWidth: 170, pointerEvents: "none" }}>
      <div style={{ color: "var(--text-tertiary)", marginBottom: 6 }}>{date}</div>
      {isCrisis   && <div style={{ marginBottom: 5, color: "var(--danger)",  fontSize: 9, fontWeight: 700 }}>⚠ LOSS DETECTED</div>}
      {isAction   && <div style={{ marginBottom: 5, color: "var(--action)",  fontSize: 9, fontWeight: 700 }}>⚡ ACTION TRIGGERED</div>}
      {isRecovery && <div style={{ marginBottom: 5, color: "var(--success)", fontSize: 9, fontWeight: 700 }}>✓ RECOVERY</div>}
      {payload.map(p => {
        if (p.dataKey === "ghost") return (
          <div key="ghost" style={{ color: "var(--text-muted)", display: "flex", justifyContent: "space-between", gap: 16, marginBottom: 2, fontSize: 10 }}>
            <span>Without VIRAJ</span><span>−€{Number(p.value ?? 0).toFixed(0)}</span>
          </div>
        );
        return (
          <div key={p.dataKey} style={{ color: p.color, display: "flex", justifyContent: "space-between", gap: 16, marginBottom: 2 }}>
            <span>{String(p.dataKey).toUpperCase()}</span>
            <span>{p.dataKey === "roi" ? fmtRoi(p.value) : `€${Number(p.value ?? 0).toFixed(0)}`}</span>
          </div>
        );
      })}
    </div>
  );
}

function ActionLabel({ viewBox }) {
  if (!viewBox) return null;
  return <text x={viewBox.x + 4} y={(viewBox.y ?? 10) + 12} fill="var(--danger)" fontSize={8} fontFamily="monospace">ACTION</text>;
}
function CrisisLabel({ viewBox }) {
  if (!viewBox) return null;
  return <text x={viewBox.x + (viewBox.width ?? 0) / 2} y={(viewBox.y ?? 10) + 14} textAnchor="middle" fill="rgba(240,64,64,0.45)" fontSize={7} fontFamily="monospace">CRISIS</text>;
}
function RecoveryLabel({ viewBox }) {
  if (!viewBox) return null;
  return <text x={viewBox.x + (viewBox.width ?? 0) / 2} y={(viewBox.y ?? 10) + 14} textAnchor="middle" fill="rgba(16,217,126,0.45)" fontSize={7} fontFamily="monospace">RECOVERY</text>;
}

export default function FinanceChartV10({ data, height = 220, riskHistory }) {
  const dynamic = buildDynamicZones(riskHistory);
  const CRISIS_ZONES    = dynamic?.crisisZones    ?? STATIC_CRISIS_ZONES;
  const RECOVERY_ZONES  = dynamic?.recoveryZones  ?? STATIC_RECOVERY_ZONES;
  const ACTION_DATES    = dynamic?.actionDates    ?? STATIC_ACTION_DATES;
  const CRISIS_DATES    = dynamic?.crisisDates    ?? STATIC_CRISIS_DATES;
  const RECOVERY_DATES  = dynamic?.recoveryDates  ?? STATIC_RECOVERY_DATES;

  const chartData = (Array.isArray(data) ? data : []).map(d => {
    const spend = Number(d.spend) || 0;
    const roi   = typeof d.roi === "number" && Math.abs(d.roi) < 10 ? d.roi * 100 : d.roi ?? 0;
    let ghost = null;
    if (CRISIS_DATES.includes(d.date) || ACTION_DATES.includes(d.date)) {
      const lossRate = roi < 0 ? Math.abs(roi) / 100 : 0.2;
      ghost = Math.round(spend * (1 + lossRate));
    }
    return { ...d, roi, ghost };
  });

  const lastRealIdx = (() => {
    for (let i = chartData.length - 1; i >= 0; i--) {
      if (Number(chartData[i].spend) > 0 || Number(chartData[i].revenue) > 0) return i;
    }
    return chartData.length - 1;
  })();
  const visible = chartData.slice(0, lastRealIdx + 1).slice(-30);

  return (
    <div style={{ width: "100%" }}>
        {visible.length === 0 ? (
          <div style={{ height, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-muted)", fontFamily: "var(--font-mono)", fontSize: 12 }}>
            No performance data available for this period.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={height}>
            <ComposedChart data={visible} margin={{ top: 12, right: 8, left: -16, bottom: 0 }}>
              <defs>
                <linearGradient id="finRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor="#10d97e" stopOpacity={0.10} />
                  <stop offset="100%" stopColor="#10d97e" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="finSpend" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor="#f04040" stopOpacity={0.07} />
                  <stop offset="100%" stopColor="#f04040" stopOpacity={0} />
                </linearGradient>
              </defs>

              <CartesianGrid stroke="var(--border-subtle)" strokeDasharray="3 4" vertical={false} />

              {CRISIS_ZONES.map((z, i) => (
                <ReferenceArea key={`c${i}`} x1={z.x1} x2={z.x2} fill="rgba(240,64,64,0.06)" strokeOpacity={0} label={i === 0 ? <CrisisLabel /> : undefined} />
              ))}
              {RECOVERY_ZONES.map((z, i) => (
                <ReferenceArea key={`r${i}`} x1={z.x1} x2={z.x2} fill="rgba(16,217,126,0.05)" strokeOpacity={0} label={i === 0 ? <RecoveryLabel /> : undefined} />
              ))}

              <XAxis dataKey="date" stroke="var(--border)" tick={{ fontFamily: "var(--font-mono)", fontSize: 10, fill: "var(--text-tertiary)" }} tickLine={false} axisLine={false} tickFormatter={v => v ? String(v).slice(5) : ""} interval="preserveStartEnd" />
              <YAxis stroke="var(--border)" tick={{ fontFamily: "var(--font-mono)", fontSize: 10, fill: "var(--text-tertiary)" }} tickLine={false} axisLine={false} />
              <Tooltip content={<ChartTooltip crisisDates={CRISIS_DATES} actionDates={ACTION_DATES} recoveryDates={RECOVERY_DATES} />} />

              {ACTION_DATES.map((d, i) => (
                <ReferenceLine key={d} x={d} stroke="var(--danger)" strokeDasharray="3 3" strokeWidth={1.5} strokeOpacity={0.55} label={i === 0 ? <ActionLabel /> : undefined} />
              ))}

              <Line type="monotone" dataKey="ghost" stroke="var(--text-tertiary)" strokeWidth={1} strokeDasharray="4 4" dot={false} activeDot={false} connectNulls={false} />
              <Area type="monotone" dataKey="revenue" stroke="#10d97e" strokeWidth={1.5} fill="url(#finRevenue)" dot={false} />
              <Area type="monotone" dataKey="spend"   stroke="var(--danger)" strokeWidth={1.0} fill="url(#finSpend)" dot={false} />
              <Line type="monotone" dataKey="roi" stroke="rgba(180,180,190,0.55)" strokeWidth={0.8} dot={false} activeDot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}

        <div style={{ display: "flex", gap: 16, marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--border-subtle)", flexWrap: "wrap", alignItems: "center" }}>
          {[
            { label: "Revenue",       color: "#10d97e",                dash: false },
            { label: "Spend",         color: "var(--danger)",          dash: false },
            { label: "ROI",           color: "rgba(180,180,190,0.55)", dash: false },
            { label: "Without VIRAJ", color: "var(--text-tertiary)",   dash: true  },
            { label: "Action",        color: "var(--danger)",          isRef: true },
          ].map(item => (
            <div key={item.label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
              {item.isRef
                ? <div style={{ width: 2, height: 12, background: item.color, opacity: 0.6 }} />
                : <svg width="16" height="8"><line x1="0" y1="4" x2="16" y2="4" stroke={item.color} strokeWidth={item.dash ? 1 : 1.5} strokeDasharray={item.dash ? "4 2" : "0"} /></svg>
              }
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 8, color: "var(--text-muted)", letterSpacing: "0.07em" }}>
                {item.label.toUpperCase()}
              </span>
            </div>
          ))}
        </div>
    </div>
  );
}
